import React, { useEffect, useState } from 'react';
import { AlertContact, AlertRule } from '../../types';
import { sectionStyle, btnStyle, inputStyle, labelStyle } from './surveillanceShared';
import { fetchAlertContacts, fetchAlertRules, fetchContactGroups, createContactGroup, updateContactGroup, deleteContactGroup, setContactGroupRules, ContactGroup } from '../../services/alertsApi';
import { fetchAdvancedRules, AdvancedRule } from '../../services/advancedRulesApi';
import ErrorBanner from '../shared/ErrorBanner';

/**
 * Kontaktgrupper: en grupp samlar flera kontakter, t.ex. "Köket". En grupp kan vara
 * mottagare för larmregler och ansvarig, andra kontakt eller "meddela vid klart" i
 * egenkontrollen. Medlemmarna slås upp vid utskick, så en ändring i gruppen slår
 * igenom överallt direkt.
 */
const ContactGroups: React.FC = () => {
    const [groups, setGroups] = useState<ContactGroup[]>([]);
    const [contacts, setContacts] = useState<AlertContact[]>([]);
    const [rules, setRules] = useState<AlertRule[]>([]);
    const [advancedRules, setAdvancedRules] = useState<AdvancedRule[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<ContactGroup | 'new' | null>(null);
    const [name, setName] = useState('');
    const [memberIds, setMemberIds] = useState<Set<number>>(new Set());
    const [saving, setSaving] = useState(false);
    // Koppla till regler: vilken grupp som redigeras och vilka regler som är ikryssade.
    const [assignGroup, setAssignGroup] = useState<ContactGroup | null>(null);
    const [assignRuleIds, setAssignRuleIds] = useState<Set<number>>(new Set());
    const [assignAdvancedIds, setAssignAdvancedIds] = useState<Set<number>>(new Set());
    const [assignSaving, setAssignSaving] = useState(false);

    const load = async () => {
        try {
            const [g, c, r, a] = await Promise.all([fetchContactGroups(), fetchAlertContacts(), fetchAlertRules(), fetchAdvancedRules()]);
            setGroups(g); setContacts(c); setRules(r); setAdvancedRules(a);
        } catch (e: any) { setError(e.message || 'Kunde inte hämta grupperna'); }
    };
    useEffect(() => { load(); }, []);

    // Kontakterna hämtas om när formuläret öppnas, så en kontakt som just skapats ovanför syns direkt.
    const openNew = () => { setEditing('new'); setName(''); setMemberIds(new Set()); setError(null); load(); };
    const openEdit = (g: ContactGroup) => { setEditing(g); setName(g.name); setMemberIds(new Set(g.members.map(m => m.id))); setError(null); load(); };
    const toggle = (id: number) => setMemberIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) =>
        setter(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) { setError('Gruppen måste ha ett namn'); return; }
        setSaving(true); setError(null);
        try {
            const body = { name: name.trim(), contact_ids: Array.from(memberIds) };
            if (editing === 'new') await createContactGroup(body);
            else if (editing) await updateContactGroup(editing.id, body);
            setEditing(null);
            await load();
        } catch (err: any) { setError(err.message || 'Kunde inte spara gruppen'); }
        finally { setSaving(false); }
    };

    // Bara tomma grupper får tas bort; servern nekar annars också.
    const remove = async (g: ContactGroup) => {
        if (g.members.length > 0) { setError(`Gruppen "${g.name}" har kontakter. Ta bort kontakterna ur gruppen först.`); return; }
        const extra = g.schedule_count > 0 ? ` ${g.schedule_count} schema(n) i egenkontrollen har gruppen som ansvarig och står utan ansvarig efteråt.` : '';
        if (!window.confirm(`Ta bort gruppen "${g.name}"?${extra}`)) return;
        try { await deleteContactGroup(g.id); await load(); }
        catch (err: any) { setError(err.message || 'Kunde inte ta bort gruppen'); }
    };

    const openAssign = (g: ContactGroup) => {
        setAssignGroup(g);
        setAssignRuleIds(new Set(g.rules.map(r => r.id)));
        setAssignAdvancedIds(new Set(g.advanced_rules.map(r => r.id)));
        setError(null);
        load();
    };

    const submitAssign = async () => {
        if (!assignGroup) return;
        setAssignSaving(true);
        try {
            await setContactGroupRules(assignGroup.id, Array.from(assignRuleIds), Array.from(assignAdvancedIds));
            setAssignGroup(null);
            await load();
        } catch (err: any) { setError(err.message || 'Kunde inte koppla gruppen'); }
        finally { setAssignSaving(false); }
    };

    const smsCount = (g: ContactGroup) => contacts.filter(c => g.members.some(m => m.id === c.id) && c.notify_sms).length;

    return (
        <div style={{ ...sectionStyle, marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <h3 style={{ margin: 0 }}>👥 Grupper</h3>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                        En grupp kan få larm och vara ansvarig för scheman i egenkontrollen. Alla i gruppen får utskicken, och för egenkontrollen gör den som påbörjar kontrollen klart den.
                    </div>
                </div>
                {!editing && <button onClick={openNew} style={btnStyle('#28a745')}>+ Ny grupp</button>}
            </div>

            {error && <div style={{ marginTop: 10 }}><ErrorBanner message={error} onDismiss={() => setError(null)} /></div>}

            {assignGroup && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', borderRadius: 8, padding: 24, minWidth: 340, maxWidth: 520, width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>Vilka larm ska gruppen {assignGroup.name} få?</h3>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                            Alla {assignGroup.members.length} i gruppen får de ikryssade larmen, var och en enligt sina egna kanaler och larmtider.
                            {smsCount(assignGroup) > 0 && <> <b>{smsCount(assignGroup)}</b> av dem har sms påslaget, så varje larm ger lika många sms.</>}
                        </div>
                        {rules.length === 0 && advancedRules.length === 0 ? (
                            <div style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>Inga larmregler finns ännu.</div>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1, marginBottom: 16, border: '1px solid #ddd', borderRadius: 4 }}>
                                {rules.map(rule => {
                                    const checked = assignRuleIds.has(rule.id);
                                    return (
                                        <label key={`r${rule.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: checked ? '#f0f7ff' : 'white' }}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleIn(setAssignRuleIds, rule.id)} />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>{rule.name}</div>
                                                {rule.group_name && <div style={{ fontSize: 11, color: '#888' }}>{rule.group_name}</div>}
                                            </div>
                                        </label>
                                    );
                                })}
                                {advancedRules.length > 0 && (
                                    <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#6a1b9a', backgroundColor: '#f7f2fa', borderBottom: '1px solid #f0f0f0' }}>AVANCERADE</div>
                                )}
                                {advancedRules.map(rule => {
                                    const checked = assignAdvancedIds.has(rule.id);
                                    return (
                                        <label key={`a${rule.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: checked ? '#f7f2fa' : 'white' }}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleIn(setAssignAdvancedIds, rule.id)} />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>{rule.name ?? `Regel #${rule.id}`}</div>
                                                <div style={{ fontSize: 11, color: '#888' }}>{rule.device_name ?? rule.ieee_address} · tröskel</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setAssignGroup(null)} style={btnStyle('#6c757d')} disabled={assignSaving}>Avbryt</button>
                            <button onClick={submitAssign} style={btnStyle('#007bff')} disabled={assignSaving || (rules.length === 0 && advancedRules.length === 0)}>
                                {assignSaving ? 'Sparar…' : 'Spara'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <form onSubmit={save} style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginTop: 14 }}>
                    <h4 style={{ margin: '0 0 10px' }}>{editing === 'new' ? 'Ny grupp' : 'Ändra grupp'}</h4>
                    <label style={labelStyle}>Namn</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="T.ex. Köket" style={{ ...inputStyle, maxWidth: 360 }} />
                    <div style={{ ...labelStyle, marginTop: 12 }}>Vilka ingår</div>
                    {contacts.length === 0
                        ? <div style={{ fontSize: 13, color: '#888' }}>Skapa kontakter först.</div>
                        : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                                {contacts.map(c => (
                                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={memberIds.has(c.id)} onChange={() => toggle(c.id)} />
                                        <span>{c.name}{c.email ? <span style={{ color: '#888', fontSize: 12 }}> ({c.email})</span> : ''}{c.notify_sms ? ' 📱' : ''}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                        <button type="submit" disabled={saving} style={btnStyle('#28a745')}>{saving ? 'Sparar…' : 'Spara grupp'}</button>
                        <button type="button" onClick={() => setEditing(null)} style={btnStyle('#6c757d')}>Avbryt</button>
                    </div>
                </form>
            )}

            {groups.length === 0 && !editing ? (
                <div style={{ fontSize: 13, color: '#888', marginTop: 12 }}>Inga grupper ännu.</div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 14 }}>
                    {groups.map(g => {
                        const ruleNames = [...g.rules.map(r => r.name), ...g.advanced_rules.map(r => r.name)];
                        return (
                            <div key={g.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 14, background: 'white' }}>
                                <div style={{ fontSize: 16, fontWeight: 'bold' }}>{g.name}</div>
                                <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>
                                    {g.members.length === 0 ? <span style={{ color: '#b02a37' }}>Inga medlemmar, ingen får utskicken.</span> : g.members.map(m => m.name).join(', ')}
                                </div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                                    {g.schedule_count > 0 ? `Ansvarig för ${g.schedule_count} schema${g.schedule_count === 1 ? '' : 'n'}` : 'Inte kopplad till något schema'}
                                </div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                                    {ruleNames.length > 0 ? `Larm: ${ruleNames.join(', ')}` : 'Får inga larm'}
                                </div>
                                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                                    <button onClick={() => openAssign(g)} style={{ ...btnStyle('#17a2b8'), flex: 1 }}>Koppla till regler</button>
                                    <button onClick={() => openEdit(g)} style={{ ...btnStyle('#ffc107', '#000'), flex: 1 }}>Ändra</button>
                                    <button onClick={() => remove(g)} disabled={g.members.length > 0}
                                        title={g.members.length > 0 ? 'Bara tomma grupper kan tas bort. Ta bort kontakterna ur gruppen först.' : undefined}
                                        style={{ ...btnStyle('#dc3545'), flex: 1, opacity: g.members.length > 0 ? 0.5 : 1, cursor: g.members.length > 0 ? 'not-allowed' : 'pointer' }}>Ta bort</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ContactGroups;
